import { nextTick, shallowRef } from 'vue'
import type {
  RouteLocationNormalizedLoaded,
  RouteMeta,
  RouteParamsGeneric,
  Router,
} from 'vue-router'

import { pageScrollElement } from '@/composables/usePageScroll'
import {
  historyTravel,
  navigationIdentity,
  navigationKind,
  type NavigationKind,
  type NavigationPoint,
} from '@/lib/navigation/direction'
import {
  createScrollMemory,
  historyEntryKey,
  planScroll,
  type ScrollPlan,
} from '@/lib/navigation/scroll-memory'

/** The transition the latest navigation plays; read by the shell when it mounts the new body. */
export const navigationMotion = shallowRef<NavigationKind>('none')

/** How long a restored scroll waits for the page to grow tall enough, in milliseconds. */
const RESTORE_WAIT_MS = 800

/** The key a mounted routed view lives under: sections and same-`viewKey` routes keep one view across their routes. */
export function routedViewKey(route: {
  path: string
  params: RouteParamsGeneric
  meta: RouteMeta
  matched: readonly { path: string }[]
}): string {
  const section = route.matched.length > 1 ? route.matched[0] : undefined
  const viewKey = route.meta.viewKey
  return (
    section?.path ?? (typeof viewKey === 'function' ? viewKey(route.params) : viewKey) ?? route.path
  )
}

function pointOf(route: RouteLocationNormalizedLoaded): NavigationPoint {
  return {
    identity: navigationIdentity(route.name, route.path, route.params),
    depth: route.meta.depth,
  }
}

function historyPosition(): number | null {
  const position: unknown = window.history.state?.position
  return typeof position === 'number' ? position : null
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function whenTallEnough(scroller: HTMLElement, top: number, apply: () => void): void {
  const startedAt = performance.now()
  const check = (): void => {
    const reachable = scroller.scrollHeight - scroller.clientHeight
    if (reachable >= top || performance.now() - startedAt >= RESTORE_WAIT_MS) {
      apply()
      return
    }
    requestAnimationFrame(check)
  }
  check()
}

function applyScroll(plan: ScrollPlan): void {
  const scroller = pageScrollElement.value
  if (!scroller || plan.kind === 'keep') return
  if (plan.kind === 'top') {
    scroller.scrollTop = 0
    return
  }
  if (plan.kind === 'restore') {
    whenTallEnough(scroller, plan.top, () => (scroller.scrollTop = plan.top))
    return
  }
  const target = document.getElementById(decodeURIComponent(plan.hash.slice(1)))
  if (target) target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
}

/** Tracks how each navigation reads (for the body motion) and keeps the page's scroll per history entry. */
export function installNavigationEffects(router: Router): void {
  const memory = createScrollMemory()
  let from: NavigationPoint | null = null
  let position = historyPosition()
  let entry = historyEntryKey(position, router.currentRoute.value.fullPath)
  let walked = false

  router.options.history.listen(() => {
    walked = true
  })

  router.beforeEach(() => {
    const scroller = pageScrollElement.value
    if (scroller) memory.save(entry, scroller.scrollTop)
  })

  router.afterEach((to, _from, failure) => {
    const popped = walked
    walked = false
    if (failure) return

    const nextPosition = historyPosition()
    const travel = popped ? historyTravel(position, nextPosition) : 'none'
    const point = pointOf(to)
    const kind = navigationKind(from, point, travel)

    navigationMotion.value = kind
    position = nextPosition
    entry = historyEntryKey(position, to.fullPath)
    from = point

    const plan = planScroll({
      pageChanged: kind !== 'none',
      popped,
      hash: to.hash,
      remembered: memory.recall(entry),
    })
    void nextTick(() => applyScroll(plan))
  })
}
